const HEADER_NAMES = new Set(['filename', 'name', 'file', 'filenames', 'files']);

/**
 * Strip quotes/whitespace and return basename (keeps original casing).
 */
export function normalizeBasename(name) {
    if (name == null) return '';
    let text = String(name).trim().replace(/^['"]+|['"]+$/g, '').trim();
    if (!text) return '';
    text = text.replace(/\\/g, '/');
    const parts = text.split('/');
    return (parts[parts.length - 1] || '').trim();
}

function splitCsvLine(line) {
    const cells = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
        const ch = line[i];
        if (inQuotes) {
            if (ch === '"') {
                if (line[i + 1] === '"') {
                    current += '"';
                    i += 1;
                } else {
                    inQuotes = false;
                }
            } else {
                current += ch;
            }
        } else if (ch === '"') {
            inQuotes = true;
        } else if (ch === ',') {
            cells.push(current.trim());
            current = '';
        } else {
            current += ch;
        }
    }
    cells.push(current.trim());
    return cells;
}

function firstNonEmptyCell(cells) {
    for (const cell of cells) {
        const value = String(cell || '').trim().replace(/^['"]+|['"]+$/g, '').trim();
        if (value) return value;
    }
    return '';
}

function looksLikeHeader(value) {
    return HEADER_NAMES.has(String(value || '').trim().toLowerCase());
}

/**
 * Parse a textarea: one filename per line. Empty lines ignored.
 */
export function parseFilenameLines(text) {
    if (!text) return [];
    return String(text)
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => normalizeBasename(line) || line.trim())
        .filter(Boolean);
}

/**
 * Parse CSV text: first non-empty column per row.
 * Drops a header row when it looks like filename/name/file.
 */
export function parseCsvFilenames(text) {
    if (!text) return { names: [], skippedHeader: false };
    const lines = String(text).split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    if (!lines.length) return { names: [], skippedHeader: false };

    let skippedHeader = false;
    const start = 0;
    const firstCell = firstNonEmptyCell(splitCsvLine(lines[0]));
    let index = start;
    if (looksLikeHeader(firstCell)) {
        skippedHeader = true;
        index = 1;
    }

    const names = [];
    for (let i = index; i < lines.length; i += 1) {
        const value = firstNonEmptyCell(splitCsvLine(lines[i]));
        const basename = normalizeBasename(value);
        if (basename) names.push(basename);
    }
    return { names, skippedHeader };
}

/**
 * Combine pasted lines + optional CSV into a de-duplicated name list
 * (case-insensitive basename, first occurrence wins).
 */
export function parseFilenameInput({ text = '', csvText = '' } = {}) {
    const fromText = parseFilenameLines(text);
    const csv = parseCsvFilenames(csvText);
    const names = [];
    const seen = new Set();
    for (const name of [...fromText, ...csv.names]) {
        const key = String(name).toLowerCase();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        names.push(name);
    }
    return {
        names,
        skippedHeader: csv.skippedHeader,
    };
}
